import { FC, useEffect, useState } from 'react';
import Lightbox from 'yet-another-react-lightbox';
import { c, dayjs } from '../../lib/util';
import { ApiPost } from '../../models/Post';
import styles from './styles.module.scss';
import Avatar from '../../components/Avatar';
import { Button, Segment } from '../../kit';
import { ssoUrl } from '../../lib/auth';
import CreatePost from './CreatePost';
import { ApiProfile } from '../../api/auth';
import { faPencil } from '@fortawesome/pro-regular-svg-icons';
import { openPostModal } from './PostModal';
import 'yet-another-react-lightbox/styles.css';

interface PostProps {
    auth: ApiProfile,
    item: ApiPost,
}

const Post: FC<PostProps> = ({ auth, item }): JSX.Element => {
    const [ expand, setExpand ] = useState(false);
    const [ children, setChildren ] = useState<ApiPost[] | undefined>(item.children);
    const [ slides, setSlides ] = useState<{ src: string }[]>([]);
    const [ showLightbox, setShowLightbox ] = useState<boolean>(false);

    useEffect(() => {
        setExpand(item.content.length <= 270);
        setChildren(item.children);

        const newSlides: { src: string }[] = [];
        item.files.forEach((file) => {
            if (file.type === 'image') {
                newSlides.push({ src: file.url || '' });
            }
        });
        setSlides(newSlides);
    }, [item]);

    return (<>
        <Segment
            className={c([
                styles.post,
                item.type === 2 && styles.notification,
                item.parentId && styles.child
            ])}
            data-id={`${item.id}`}
        >
            {item.type === 1 && (
                <div className={styles.postMeta}>
                    <Avatar profile={item.createdBy} size={32} />
                    <div className={styles.postMetaInfo}>
                        <a href={`/community/${item.createdBy.permalink}`}>
                            {item.createdBy.name}
                        </a>
                        <small>{dayjs(item.createdAt).format('D MMMM YYYY - HH:mm')}</small>
                    </div>
                    {item.createdBy.id === auth.id && (
                        <Button
                            color="border"
                            icon={faPencil}
                            onClick={() => openPostModal(item, auth)}
                        />
                    )}
                </div>
            )}
            <div className={c([styles.postContent, expand && styles.expanded])}>
                {item.type === 1 && <div dangerouslySetInnerHTML={{ __html: item.html }} />}
                {item.type === 2 && (
                    <div className={styles.postNotification}>
                        <div className={styles.postIcon}>
                            {item.options?.type === 'exercise' && <i className="far fa-person-running" />}
                            {item.options?.type === 'knowledge' && <i className="far fa-graduation-cap" />}
                            {item.options?.type === 'event' && <i className="far fa-calendar-alt" />}
                        </div>
                        <div className={styles.postNotificationContent}>
                            <p>
                                <a href={`/community/${item.createdBy.permalink}`}>
                                    {item.createdBy.name}
                                </a>
                                {' plaatste '}
                                {item.options?.type === 'exercise' && 'een nieuwe oefening: '}
                                {item.options?.type === 'knowledge' && 'een nieuw item in de kennisbank: '}
                                {item.options?.type === 'event' && `een nieuw evenement voor ${dayjs(item.options?.date).format('DD MMMM YYYY - HH:mm')}: `}
                                <a className="external" href={ssoUrl(`/${item.options?.type?.replace('knowledge', 'kb')}s/${item.options?.id}`).replace('commapi/', '')}>
                                    {item.options?.name}
                                </a>
                            </p>
                            <Avatar profile={item.createdBy} size={32} />
                        </div>
                    </div>
                )}
            </div>
            {(
                item.files.length > 1 ||
                (item.files.length === 1 && item.files[0].type === 'document') ||
                (item.parentId && item.files.length > 0)
            ) && (
                <div className={styles.attachments}>
                    {item.files.map((file, index) => (
                        <a
                            key={`attachment-${index}`}
                            className={styles.attachment}
                            href={file.url}
                            target="_blank"
                            rel="noopener norefferer"
                            onClick={file.type === 'image' ? (e) => {
                                e.preventDefault();
                                setShowLightbox(true);
                            } : undefined}
                        >
                            {file.type === 'video' && <i className="far fa-video" />}
                            {file.type === 'document' && <i className="far fa-file-pdf" />}
                            {file.type === 'image' && (
                                <div style={{ backgroundImage: `url(${file.preview})` }} />
                            )}
                        </a>
                    ))}
                </div>
            )}
            {!expand && (
                <div
                    className={styles.expand}
                    onClick={() => setExpand(true)}
                >
                    Meer weergeven <i className="far fa-arrow-down" />
                </div>
            )}
            {item.options?.youtube && (
                <div className={styles.postVideo}>
                    <div className="ratio ratio-16x9">
                        <iframe src={`https://www.youtube.com/embed/${item.options.youtube}`} />
                    </div>
                </div>
            )}
            {item.options?.vimeo && (
                <div className={styles.postVideo}>
                    <div className="ratio ratio-16x9">
                        <iframe src={`https://player.vimeo.com/video/${item.options.vimeo}?byline=0&portrait=0`} />
                    </div>
                </div>
            )}
            {!item.parentId && item.files.length === 1 && item.files[0].type === 'video' && (
                <div className={styles.postVideo}>
                    <div className="ratio ratio-16x9">
                        <video src={item.files[0].url} controls />
                    </div>
                </div>
            )}
            {!item.parentId && item.files.length === 1 && item.files[0].type === 'image' && (
                <div className={styles.postHero}>
                    <img src={item.files[0].url} alt="" />
                </div>
            )}
            {children && children.length > 0 && (
                <div className={styles.postReactions}>
                    {children.map((child) => (
                        <Post
                            key={`child-${child.id}`}
                            auth={auth}
                            item={child}
                        />
                    ))}
                </div>
            )}
            {!item.parentId && (
                <div className={styles.postActions}>
                    <CreatePost
                        auth={auth}
                        parent={item}
                        onPost={(post) => {
                            setChildren((c) => ([
                                ...(c || []),
                                post,
                            ]));
                        }}
                    />
                </div>
            )}
        </Segment>
        {slides.length > 0 && (
            <Lightbox
                open={showLightbox}
                close={() => setShowLightbox(false)}
                slides={slides}
            />
        )}
    </>);
}

export default Post;
